import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconCopy,
  IconInfoCircle,
  IconKey,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconUserPlus,
} from '@tabler/icons-react';
import {
  createStaffSchema,
  resetStaffPasswordSchema,
  updateStaffSchema,
  type StaffDto,
  type StaffRole,
} from '@expeditor/shared';
import { adminStaffApi } from '../../shared/api/admin-staff';

type CreateForm = {
  email: string;
  fullName: string;
  phone: string;
  role: StaffRole;
  password: string;
};

const emptyCreate: CreateForm = {
  email: '',
  fullName: '',
  phone: '',
  role: 'MANAGER',
  password: '',
};

/** Сильный временный пароль: 16 символов, включает буквы, цифры, спецсимвол. */
function generatePassword(): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const special = '!@#$%&*';
  const all = upper + lower + digits + special;
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
  const chars = [pick(upper), pick(lower), pick(digits), pick(special)];
  for (let i = chars.length; i < 16; i++) chars.push(pick(all));
  return chars.sort(() => Math.random() - 0.5).join('');
}

const roleLabel: Record<StaffRole, string> = {
  MANAGER: 'Менеджер',
  ADMIN: 'Администратор',
  AUDITOR: 'Аудитор',
};

export function StaffPage() {
  const [staff, setStaff] = useState<StaffDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Создание
  const [createOpened, setCreateOpened] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(emptyCreate);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdHint, setCreatedHint] = useState<{ email: string; password: string } | null>(null);

  // Редактирование
  const [editing, setEditing] = useState<StaffDto | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editRole, setEditRole] = useState<StaffRole>('MANAGER');
  const [editCanAct, setEditCanAct] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Сброс пароля
  const [resetting, setResetting] = useState<StaffDto | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState<{ email: string; password: string } | null>(null);

  const load = () => {
    setLoading(true);
    adminStaffApi
      .list()
      .then(setStaff)
      .catch((err) => setError(err instanceof Error ? err.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (s: StaffDto) => {
    setEditing(s);
    setEditEmail(s.email);
    setEditFullName(s.fullName ?? '');
    setEditPhone(s.phone ?? '');
    setEditRole(s.role);
    setEditCanAct(s.canActForClients);
    setEditError(null);
  };

  const openReset = (s: StaffDto) => {
    setResetting(s);
    setNewPassword('');
    setResetError(null);
    setResetDone(null);
  };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    const parsed = createStaffSchema.safeParse(createForm);
    if (!parsed.success) {
      setCreateError(parsed.error.issues.map((i) => i.message).join('; '));
      return;
    }
    setCreateSubmitting(true);
    try {
      const created = await adminStaffApi.create(createForm);
      setStaff((s) => [created, ...s]);
      setCreatedHint({ email: createForm.email, password: createForm.password });
      setCreateForm(emptyCreate);
      setCreateOpened(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Не удалось создать');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setEditError(null);
    const payload = {
      email: editEmail,
      fullName: editFullName,
      phone: editPhone,
      role: editRole,
      canActForClients: editCanAct,
    };
    const parsed = updateStaffSchema.safeParse(payload);
    if (!parsed.success) {
      setEditError(parsed.error.issues.map((i) => i.message).join('; '));
      return;
    }
    setEditSubmitting(true);
    try {
      const updated = await adminStaffApi.update(editing.id, payload);
      setStaff((prev) => prev.map((p) => (p.id === editing.id ? updated : p)));
      setEditing(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setEditSubmitting(false);
    }
  };

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetting) return;
    setResetError(null);
    const parsed = resetStaffPasswordSchema.safeParse({ password: newPassword });
    if (!parsed.success) {
      setResetError(parsed.error.issues.map((i) => i.message).join('; '));
      return;
    }
    setResetSubmitting(true);
    try {
      await adminStaffApi.resetPassword(resetting.id, newPassword);
      setResetDone({ email: resetting.email, password: newPassword });
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Не удалось сбросить');
    } finally {
      setResetSubmitting(false);
    }
  };

  const toggleActive = async (s: StaffDto) => {
    try {
      const updated = await adminStaffApi.setActive(s.id, !s.isActive);
      setStaff((prev) => prev.map((p) => (p.id === s.id ? updated : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить статус');
    }
  };

  const copy = (text: string) => navigator.clipboard?.writeText(text);

  if (loading) return <Loader />;

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Title order={3}>Сотрудники</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpened(true)}>
          Добавить
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconInfoCircle size={16} />} mb="md">
          {error}
        </Alert>
      )}

      {createdHint && (
        <Alert
          color="green"
          icon={<IconUserPlus size={16} />}
          mb="md"
          withCloseButton
          onClose={() => setCreatedHint(null)}
        >
          Сотрудник <strong>{createdHint.email}</strong> создан. Временный пароль:{' '}
          <code>{createdHint.password}</code>{' '}
          <Tooltip label="Скопировать">
            <ActionIcon size="sm" variant="subtle" onClick={() => copy(createdHint.password)}>
              <IconCopy size={14} />
            </ActionIcon>
          </Tooltip>
        </Alert>
      )}

      <Table striped withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Email</Table.Th>
            <Table.Th>ФИО</Table.Th>
            <Table.Th>Роль</Table.Th>
            <Table.Th>Телефон</Table.Th>
            <Table.Th>Активен</Table.Th>
            <Table.Th>Создан</Table.Th>
            <Table.Th>Последний вход</Table.Th>
            <Table.Th style={{ width: 90 }}>Действия</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {staff.map((s) => (
            <Table.Tr key={s.id} opacity={s.isActive ? 1 : 0.55}>
              <Table.Td>{s.email}</Table.Td>
              <Table.Td>{s.fullName ?? '—'}</Table.Td>
              <Table.Td>
                <Badge
                  color={s.role === 'ADMIN' ? 'red' : s.role === 'AUDITOR' ? 'orange' : 'blue'}
                  variant="light"
                >
                  {roleLabel[s.role]}
                </Badge>
                {s.role === 'MANAGER' && s.canActForClients && (
                  <Badge color="grape" variant="light" mt={4}>
                    за клиента
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>{s.phone ?? '—'}</Table.Td>
              <Table.Td>
                <Switch checked={s.isActive} onChange={() => toggleActive(s)} />
              </Table.Td>
              <Table.Td>{new Date(s.createdAt).toLocaleDateString('ru-RU')}</Table.Td>
              <Table.Td>
                {s.lastLoginAt ? new Date(s.lastLoginAt).toLocaleString('ru-RU') : '—'}
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  <Tooltip label="Редактировать">
                    <ActionIcon variant="subtle" onClick={() => openEdit(s)}>
                      <IconPencil size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Сбросить пароль">
                    <ActionIcon variant="subtle" color="orange" onClick={() => openReset(s)}>
                      <IconKey size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {staff.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={8}>
                <Text c="dimmed" ta="center" py="md">
                  Сотрудников пока нет
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      {/* --- Создание --- */}
      <Modal
        opened={createOpened}
        onClose={() => setCreateOpened(false)}
        title="Новый сотрудник"
        size="md"
      >
        <form onSubmit={submitCreate}>
          <Stack>
            <TextInput
              label="Email"
              type="email"
              required
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.currentTarget.value })}
              autoComplete="off"
            />
            <TextInput
              label="ФИО"
              required
              value={createForm.fullName}
              onChange={(e) => setCreateForm({ ...createForm, fullName: e.currentTarget.value })}
              placeholder="Иванов Иван Иванович"
            />
            <TextInput
              label="Телефон"
              value={createForm.phone}
              onChange={(e) => setCreateForm({ ...createForm, phone: e.currentTarget.value })}
              placeholder="+7 ..."
            />
            <Select
              label="Роль"
              required
              value={createForm.role}
              onChange={(v) => v && setCreateForm({ ...createForm, role: v as StaffRole })}
              data={[
                { value: 'MANAGER', label: 'Менеджер' },
                { value: 'AUDITOR', label: 'Аудитор (только просмотр)' },
                { value: 'ADMIN', label: 'Администратор' },
              ]}
            />
            <Group align="flex-end" gap="xs">
              <PasswordInput
                label="Временный пароль"
                description="≥8 символов: заглавная, цифра, спецсимвол"
                required
                style={{ flex: 1 }}
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.currentTarget.value })}
                autoComplete="new-password"
              />
              <Tooltip label="Сгенерировать">
                <ActionIcon
                  variant="default"
                  size="lg"
                  onClick={() => setCreateForm({ ...createForm, password: generatePassword() })}
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            {createError && (
              <Alert color="red" icon={<IconInfoCircle size={16} />}>
                {createError}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreateOpened(false)}>
                Отмена
              </Button>
              <Button type="submit" loading={createSubmitting}>
                Создать
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* --- Редактирование --- */}
      <Modal
        opened={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing ? `Редактирование: ${editing.email}` : ''}
        size="md"
      >
        <form onSubmit={submitEdit}>
          <Stack>
            <TextInput
              label="Email"
              type="email"
              required
              value={editEmail}
              onChange={(e) => setEditEmail(e.currentTarget.value)}
            />
            <TextInput
              label="ФИО"
              required
              value={editFullName}
              onChange={(e) => setEditFullName(e.currentTarget.value)}
            />
            <TextInput
              label="Телефон"
              value={editPhone}
              onChange={(e) => setEditPhone(e.currentTarget.value)}
            />
            <Select
              label="Роль"
              required
              value={editRole}
              onChange={(v) => v && setEditRole(v as StaffRole)}
              data={[
                { value: 'MANAGER', label: 'Менеджер' },
                { value: 'AUDITOR', label: 'Аудитор (только просмотр)' },
                { value: 'ADMIN', label: 'Администратор' },
              ]}
            />
            {editRole === 'MANAGER' && (
              <Switch
                label="Действовать от имени клиента"
                description="Править данные клиента, создавать и редактировать его поручения (в пределах своих клиентов)"
                checked={editCanAct}
                onChange={(e) => setEditCanAct(e.currentTarget.checked)}
              />
            )}
            {editError && (
              <Alert color="red" icon={<IconInfoCircle size={16} />}>
                {editError}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditing(null)}>
                Отмена
              </Button>
              <Button type="submit" loading={editSubmitting}>
                Сохранить
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* --- Сброс пароля --- */}
      <Modal
        opened={Boolean(resetting)}
        onClose={() => {
          setResetting(null);
          setResetDone(null);
        }}
        title={resetting ? `Сброс пароля: ${resetting.email}` : ''}
        size="md"
      >
        {resetDone ? (
          <Stack>
            <Alert color="green" icon={<IconKey size={16} />}>
              Пароль сброшен. Передайте сотруднику новый временный пароль:{' '}
              <code>{resetDone.password}</code>{' '}
              <Tooltip label="Скопировать">
                <ActionIcon size="sm" variant="subtle" onClick={() => copy(resetDone.password)}>
                  <IconCopy size={14} />
                </ActionIcon>
              </Tooltip>
              <Text size="xs" c="dimmed" mt={6}>
                Активные сессии прекращены — сотруднику нужно залогиниться заново.
              </Text>
            </Alert>
            <Group justify="flex-end">
              <Button
                onClick={() => {
                  setResetting(null);
                  setResetDone(null);
                }}
              >
                Готово
              </Button>
            </Group>
          </Stack>
        ) : (
          <form onSubmit={submitReset}>
            <Stack>
              <Text size="sm" c="dimmed">
                Будут аннулированы все активные сессии сотрудника.
              </Text>
              <Group align="flex-end" gap="xs">
                <PasswordInput
                  label="Новый временный пароль"
                  description="≥8 символов: заглавная, цифра, спецсимвол"
                  required
                  style={{ flex: 1 }}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.currentTarget.value)}
                  autoComplete="new-password"
                />
                <Tooltip label="Сгенерировать">
                  <ActionIcon
                    variant="default"
                    size="lg"
                    onClick={() => setNewPassword(generatePassword())}
                  >
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              {resetError && (
                <Alert color="red" icon={<IconInfoCircle size={16} />}>
                  {resetError}
                </Alert>
              )}
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setResetting(null)}>
                  Отмена
                </Button>
                <Button type="submit" color="orange" loading={resetSubmitting}>
                  Сбросить
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>
    </Box>
  );
}
